import { useRequestURL, useRoute } from '#app'
import { computed } from 'vue'
import { useViewer } from '~/composables/use-login'
import {
    layoutModeGroupLink,
    layoutModeKioskLink,
    layoutModeLightboxLink
} from '~/composables/routing'

export function useLayoutMode() {
    const route = useRoute()
    const url = useRequestURL()
    const isPrintMode = computed(
        () => typeof route.query.print === 'string' && route.query.print !== ''
    )
    const printMode = computed(() => route.query.print)
    const isMobileApp = computed(() => {
        if (import.meta.env.DEV) {
            if (
                // Tunnels rewrite Host for the server, but the WebView keeps
                // the public hostname. Recognize the native app before GraphQL
                // initializes so it sends bearer tokens and enables token refresh.
                url.hostname.endsWith('.ngrok-free.dev') ||
                url.hostname.toLowerCase().endsWith('-mobile.localhost') ||
                url.hostname.toLowerCase().startsWith('192.168') ||
                url.hostname.toLowerCase().startsWith('10.')
            ) {
                return true
            }
        }

        return url.hostname.toLowerCase().endsWith('-mobile.rphq.com')
    })

    const viewer = useViewer()
    // Development and native inbox access are always available; production browser access requires an active device.
    const isInboxEnabled = computed(
        () =>
            import.meta.env.DEV ||
            isMobileApp.value ||
            viewer.value?.hasActiveMobileAppDevice === true
    )

    const layoutMode = computed(() => route.params.mode)
    const isLightbox = computed(() => {
        return layoutMode.value === 'lightbox' || route.name === 'lightbox'
    })
    const kioskSlug = computed(() => {
        return typeof layoutMode.value === 'string' &&
            layoutMode.value.startsWith('kiosk-')
            ? layoutMode.value.substring(6)
            : null
    })
    const isKiosk = computed(() => kioskSlug.value !== null)

    const groupBookingId = computed(() => {
        return typeof layoutMode.value === 'string' &&
            layoutMode.value.startsWith('group-')
            ? layoutMode.value.substring(6)
            : null
    })
    const isGroup = computed(() => groupBookingId.value !== null)

    function preserveModeLink(url: string) {
        if (isLightbox.value) {
            return layoutModeLightboxLink(url)
        }

        if (kioskSlug.value) {
            return layoutModeKioskLink(kioskSlug.value, url)
        }

        if (groupBookingId.value) {
            return layoutModeGroupLink(groupBookingId.value, url)
        }

        return url
    }

    return {
        isPrintMode,
        printMode,
        isMobileApp,
        isInboxEnabled,
        layoutMode,
        isLightbox,
        isKiosk,
        kioskSlug,
        isGroup,
        groupBookingId,
        preserveModeLink
    }
}
